import { useState } from "react";
import { useParams, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MessageCircle } from "lucide-react";
import { chaveTelefone, mascararTelefoneBR, telefoneWhatsappValido } from "@shared/mercado";
import { useDetalhesDoProdutor, useOfertaPublica, useResponderOferta } from "@/hooks/use-ofertas";
import { BlocoOferta } from "@/components/mercado/bloco-oferta";

/**
 * `/o/:token` — a oferta na praça, aberta por quem NÃO tem conta.
 *
 * Mobile-first e sem menu, igual ao `/v/:token`: o uso real é o comprador
 * abrindo no celular, no meio do dia, para dizer que tem interesse. Nada aqui
 * navega para dentro do app — a única porta é o "Entrar" do rodapé.
 *
 * ⚠️ A oferta é ANÔNIMA. Esta tela não sabe de quem é a carga (o DTO não conta),
 * e o bloco do produtor só aparece depois que ele abre os detalhes para ESTE
 * telefone — por outra rota, que responde 404 para todo o resto.
 */

/** O telefone fica no aparelho para o link reabrir já sabendo quem é. É dado do
 *  próprio dono do celular, e nunca sai daqui a não ser na resposta. */
const CHAVE_LOCAL = "rizoma:oferta:telefone";

function lerTelefoneSalvo(): string | null {
  try {
    return localStorage.getItem(CHAVE_LOCAL);
  } catch {
    // Safari em navegação privada barra o storage. Sem memória, a pessoa digita
    // de novo — o que não pode é a tela quebrar por causa disso.
    return null;
  }
}

function salvarTelefone(valor: string) {
  try {
    localStorage.setItem(CHAVE_LOCAL, valor);
  } catch {
    // Idem: perder a memória é aceitável, quebrar a página não.
  }
}

export default function OfertaPublicaPage() {
  const { token } = useParams<{ token: string }>();
  const [, setLocation] = useLocation();
  const [telefone, setTelefone] = useState<string | null>(() => lerTelefoneSalvo());

  const { data, isLoading, isError } = useOfertaPublica(token, telefone);
  const responder = useResponderOferta(token);
  const detalhes = useDetalhesDoProdutor(token, telefone, !!data?.respondida?.detalhesAbertos);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 text-sm text-muted-foreground">
        Abrindo a oferta…
      </div>
    );
  }

  // Token inexistente e rascunho chegam aqui como o MESMO 404 — a tela não tenta
  // adivinhar qual dos dois foi, porque o servidor de propósito não conta.
  if (isError || !data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-2 p-6 text-center">
        <p className="font-bold">Esta oferta não está mais disponível.</p>
        <p className="text-sm text-muted-foreground max-w-xs">
          Peça um link novo para quem mandou a chamada.
        </p>
      </div>
    );
  }

  // PARCIAL saiu com a 059: sem kg no fechamento, não existe carga pela metade.
  const aberta = data.status === "NA_PRACA";
  const respondida = data.respondida;

  return (
    <div className="min-h-screen bg-muted/30">
      <div className="mx-auto max-w-md p-4 space-y-4">
        <header className="rounded-xl border bg-background p-4">
          <p className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
            Oferta na praça de {data.praca.nome}
          </p>
          <BlocoOferta oferta={data} />
          <p className="text-xs text-muted-foreground mt-3">Produtor da região, no Rizoma.</p>
        </header>

        {!aberta && (
          <section className="rounded-xl border bg-background p-4 text-center space-y-1" data-testid="oferta-encerrada">
            <p className="text-sm font-bold">
              {data.status === "FECHADA" ? "Esta oferta já fechou." : "Esta oferta já passou."}
            </p>
            <p className="text-xs text-muted-foreground">
              {data.status === "FECHADA"
                ? "A carga foi fechada com outro comprador."
                : `A data de disponibilidade era ${data.disponivelEm.substring(8, 10)}/${data.disponivelEm.substring(5, 7)}.`}
            </p>
          </section>
        )}

        {aberta && !respondida && (
          <FormResposta
            salvando={responder.isPending}
            erro={responder.isError ? "Não foi possível registrar. Tente de novo." : null}
            onResponder={(dados) => {
              const chave = chaveTelefone(dados.telefone);
              responder.mutate(dados, {
                onSuccess: () => {
                  salvarTelefone(chave);
                  setTelefone(chave);
                },
              });
            }}
          />
        )}

        {aberta && respondida && (
          <section className="rounded-xl border bg-background p-4 space-y-2" data-testid="bloco-respondido">
            <p className="text-sm font-bold">Recebido.</p>
            {/* Sem "você pediu N kg": a resposta é interesse. Quanto e quando se
                acerta entre os dois, no WhatsApp — e o preço é o do dia da carga. */}
            <p className="text-xs text-muted-foreground">
              O produtor vai ver seu interesse e pode abrir os detalhes para você.
            </p>
          </section>
        )}

        {respondida?.detalhesAbertos && detalhes.data?.produtor && (
          <section className="rounded-xl border bg-background p-4 space-y-2" data-testid="bloco-produtor">
            <p className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Quem está ofertando</p>
            <p className="text-sm font-semibold">{detalhes.data.produtor.nome}</p>
            <p className="text-xs text-muted-foreground">{detalhes.data.produtor.fazenda}</p>
            {detalhes.data.produtor.telefone && (
              <Button
                className="w-full h-12"
                onClick={() => window.open(`https://wa.me/55${detalhes.data!.produtor!.telefone.replace(/\D/g, "")}`, "_blank")}
                data-testid="button-whatsapp-produtor"
              >
                <MessageCircle className="h-4 w-4 mr-1.5" />Falar no WhatsApp
              </Button>
            )}
          </section>
        )}

        <footer className="space-y-2 pb-6 text-center">
          <p className="text-[11px] text-muted-foreground">
            Este link é público; seu telefone só aparece para o produtor.
          </p>
          <button
            type="button"
            className="text-xs underline underline-offset-2 text-muted-foreground hover:text-foreground"
            onClick={() => setLocation("/login")}
            data-testid="link-entrar"
          >
            Tem conta no Rizoma? Entrar
          </button>
        </footer>
      </div>
    </div>
  );
}

/**
 * O formulário: telefone (obrigatório), nome (opcional). E um botão.
 *
 * ⚠️ NÃO pergunta kg (059). A oferta não tem preço nem pedido: o comprador diz
 * que tem interesse, e quanto/quando/quanto vale se acerta no WhatsApp, com o
 * preço da praça no dia da carga. Perguntar kg aqui faria a tela prometer um
 * acerto que ela não pode fechar.
 */
function FormResposta({
  salvando,
  erro,
  onResponder,
}: {
  salvando: boolean;
  erro: string | null;
  onResponder: (dados: { telefone: string; nome?: string | null }) => void;
}) {
  const [telefone, setTelefone] = useState("");
  const [nome, setNome] = useState("");

  const telefoneOk = telefoneWhatsappValido(telefone);

  return (
    <section className="rounded-xl border bg-background p-4 space-y-3" data-testid="form-resposta-oferta">
      <p className="text-sm font-semibold">Tem interesse nessa carga?</p>

      <div className="space-y-1.5">
        <Label htmlFor="of-telefone" className="text-xs uppercase text-muted-foreground">Seu WhatsApp</Label>
        <Input
          id="of-telefone"
          inputMode="tel"
          className="h-11"
          placeholder="(74) 99999-8888"
          value={mascararTelefoneBR(telefone)}
          onChange={e => setTelefone(e.target.value)}
          data-testid="input-telefone-oferta"
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="of-nome" className="text-xs uppercase text-muted-foreground">Seu nome (opcional)</Label>
        <Input
          id="of-nome"
          className="h-11"
          placeholder="como o produtor te conhece"
          value={nome}
          onChange={e => setNome(e.target.value)}
          data-testid="input-nome-oferta"
        />
      </div>

      {erro && <p className="text-xs text-erro" data-testid="texto-erro-oferta">{erro}</p>}

      <Button
        className="w-full h-12"
        disabled={salvando || !telefoneOk}
        onClick={() => onResponder({ telefone, nome: nome.trim() || null })}
        data-testid="button-tenho-interesse"
      >
        {salvando ? "Enviando…" : "Tenho interesse"}
      </Button>
      {!telefoneOk && telefone.length > 0 && (
        <p className="text-[11px] text-erro">Informe DDD + 9 dígitos.</p>
      )}
    </section>
  );
}
